import { readonly, ref } from 'vue'
import { buildReviewEvent, REVIEW_EVENT, REVIEW_EVENT_NAMES } from '@/utils/reviewEvents'

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════
 * EMITTING THE REVIEW VOCABULARY — one seam, and a no-op until something is wired to it
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `@/utils/reviewEvents` says WHAT may be emitted. This says how, and it has exactly one job beyond
 * calling it: make sure telemetry can never break the tool it measures.
 *
 * ── the transport is a seam, and it is empty on purpose ────────────────────────────────────────────
 *
 * The backend half does not exist yet — the endpoint, its grant and its retention are specified in
 * `docs/operations/review-telemetry.md` and are being built from that document. Until then
 * {@link setReviewTelemetryTransport} has never been called, `emit()` builds the event, counts it, and
 * drops it. That is deliberate and it is the whole point of the seam: when the endpoint lands, one
 * `setReviewTelemetryTransport(...)` at app bootstrap turns every existing call site on, and not one of
 * them changes. A composable that POSTed to a route nobody had built would spend the intervening weeks
 * emitting 404s from a reviewer's browser.
 *
 * ── never breaks the flow, and that is enforced rather than intended ───────────────────────────────
 *
 * Telemetry that can break the tool it measures is worse than no telemetry: the queue this instruments
 * had TWO verdicts against 1,093 drafts, and every one of the three causes was a screen that failed the
 * first person who tried it. So:
 *
 *   • `emit()` returns `undefined` and throws NOTHING. Build errors, transport errors, a transport that
 *     is not a function, a synchronous throw and a rejected promise are all swallowed, and the counter
 *     below is the only trace.
 *   • It is never awaited by a call site. A verdict must not wait on an analytics POST.
 *   • An unknown event name produces nothing at all. `buildReviewEvent` returns null and this drops it,
 *     rather than putting an untyped payload on the wire and redacting it afterwards.
 *
 * ── the session id, and what it is not ─────────────────────────────────────────────────────────────
 *
 * A funnel cannot be computed without stitching one reviewer's own events together, so each sitting gets
 * an opaque id. It is generated in memory, never written to storage, never derived from the user, and
 * dies with the tab — so it is not a device id, it does not follow anyone between sittings, and two
 * sittings by the same person are not linkable through it. The ACTOR is the authenticated request's and
 * is never in the payload: a client-supplied identity is both forgeable and a second source of truth.
 *
 * `seq` rides beside it because a client clock cannot be trusted to order anything. `at` is advisory;
 * the backend stamps its own arrival time and orders by `(session_id, seq)`.
 */

/** The installed sink, or null. Module-level: one workbench, one transport, replaced as a whole. */
let transport = null

/** Opaque, in-memory, per-tab. Not storage, not a device id, not derived from the user. */
const newSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch (_e) { /* locked-down context — the fallback below is fine, this id means nothing on its own */ }

  return `rvw-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

let sessionId = newSessionId()
let sessionStartedAt = Date.now()
let seq = 0

/** What this sitting has done so far, so `review.session_ended` does not have to be assembled by the page. */
const counts = ref({ opened: 0, committed: 0, revealed: 0, decided: 0 })
/** Events the vocabulary refused — an unknown name, or a build that produced nothing. Never silent. */
const dropped = ref(0)
/** Events handed to a transport. Zero while none is installed, which is the expected state today. */
const delivered = ref(0)

/** Dedupe keys already seen this sitting, for surfaces that would otherwise re-emit on every render. */
let seen = new Set()

const COUNTER_FOR = {
  [REVIEW_EVENT.ITEM_OPENED]: 'opened',
  [REVIEW_EVENT.ANSWER_COMMITTED]: 'committed',
  [REVIEW_EVENT.KEY_REVEALED]: 'revealed',
  [REVIEW_EVENT.DECISION_SUBMITTED]: 'decided',
}

/**
 * Install (or replace) the sink every emitted event is handed to.
 *
 * Called once at app bootstrap when the endpoint exists. `null` uninstalls, which is what tests use to
 * put the module back in its shipping state.
 *
 * @param {((event: {name: string, props: object, session_id: string, seq: number, at: string}) => unknown)|null} fn
 */
export function setReviewTelemetryTransport(fn) {
  transport = typeof fn === 'function' ? fn : null
}

/** Whether anything is listening. A page must never change what it emits based on this. */
export function isReviewTelemetryInstalled() {
  return transport !== null
}

/**
 * Start a fresh sitting: a new opaque id, zeroed counters, an empty dedupe set.
 *
 * Called when the workbench mounts. Without it a second visit in the same tab would continue the first
 * one's counters and its `session_ended` would report a sitting that never happened.
 */
export function startReviewTelemetrySession() {
  sessionId = newSessionId()
  sessionStartedAt = Date.now()
  seq = 0
  counts.value = { opened: 0, committed: 0, revealed: 0, decided: 0 }
  // `dropped` and `delivered` are scoped to the sitting for the same reason `counts` is: they are read
  // beside it, and a lifetime-of-tab total sitting next to a per-sitting one is two units in one row.
  dropped.value = 0
  delivered.value = 0
  seen = new Set()
}

function send(built) {
  seq += 1
  const envelope = {
    name: built.name,
    props: built.props,
    session_id: sessionId,
    seq,
    // Advisory. The backend stamps arrival and orders by (session_id, seq) — see the contract doc.
    at: new Date().toISOString(),
  }

  const counter = COUNTER_FOR[built.name]
  if (counter) counts.value = { ...counts.value, [counter]: counts.value[counter] + 1 }

  if (!transport) return

  try {
    const result = transport(envelope)
    // A rejected promise from the sink is a network fact, not a reviewer's problem. Attached BEFORE the
    // handoff is counted so an already-rejected promise cannot escape as an unhandled rejection.
    if (result && typeof result.catch === 'function') result.catch(() => {})
    delivered.value += 1
  } catch (_e) {
    // A throwing transport must not reach the call site, and must not be counted as a handoff.
    dropped.value += 1
  }
}

export function useReviewTelemetry() {
  /**
   * Emit one event. Returns nothing, throws nothing, and is never awaited.
   *
   * @param {string} name  one of {@link REVIEW_EVENT_NAMES}
   * @param {object} [props]  keys outside the event's declaration are dropped by `buildReviewEvent`
   */
  const emit = (name, props) => {
    try {
      const built = buildReviewEvent(name, props)
      if (!built) { dropped.value += 1; return }
      send(built)
    } catch (_e) {
      // The last line of defence. Nothing above this should be able to throw; if it ever does, the
      // reviewer's queue keeps working and the count says something went wrong.
      dropped.value += 1
    }
  }

  /**
   * Emit at most once per `key` for this sitting.
   *
   * `review.evidence_shown` is computed from what is on the card, so it would re-fire on every re-render
   * — a reveal, a keystroke in the note, a lease tick. An impression counted per render is not an
   * impression count, it is a render count wearing one.
   */
  const emitOnce = (key, name, props) => {
    try {
      const dedupe = `${name}::${key}`
      if (seen.has(dedupe)) return
      seen.add(dedupe)
      emit(name, props)
    } catch (_e) { /* never breaks the flow */ }
  }

  /**
   * Close the sitting, with the counters this composable kept and the elapsed seconds.
   *
   * The page calls this on unmount AND on `pagehide`, and the dedupe key makes the second one free: a
   * tab close after an unmount must not report the sitting twice, and `onBeforeUnmount` alone does not
   * survive a close, a refresh or a crash.
   */
  const endSession = () => {
    emitOnce('session', REVIEW_EVENT.SESSION_ENDED, {
      ...counts.value,
      seconds: Math.round((Date.now() - sessionStartedAt) / 1000),
    })
  }

  return {
    emit,
    emitOnce,
    endSession,
    startSession: startReviewTelemetrySession,
    counts: readonly(counts),
    dropped: readonly(dropped),
    delivered: readonly(delivered),
    names: REVIEW_EVENT_NAMES,
  }
}
